import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import useFeedComposer from '../hooks/useFeedComposer';
import { uploadFeedImage, deleteFeedUpload } from '../services/feedApi';
import { getAuthToken } from '../utils/auth';
import { localizedValue } from '../localization/presentationLanguage';
const feedText = (es, en) => localizedValue({ es, en });
const FEED_COMPOSER_DRAFT_KEY = 'feed_composer_draft_v1';

export default function FeedComposer({
  onSubmit,
  placeholder,
  submitLabel,
  disabled = false,
  maxLength = 600,
  minLength = 2,
  className = '',
  token = '',
}) {
  const resolvedPlaceholder = placeholder || feedText('¿Qué energía querés compartir hoy?', 'What energy would you like to share today?');
  const resolvedSubmitLabel = submitLabel || feedText('Publicar', 'Post');
  const {
    text,
    error,
    submitting,
    rawLength,
    remaining,
    isTooShort,
    isTooLong,
    canSubmit,
    handleChange,
    submit,
    onKeyDown,
    setText,
  } = useFeedComposer({
    onSubmit,
    maxLength,
    minLength,
  });

  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageError, setImageError] = useState('');
  const [viewportWidth, setViewportWidth] = React.useState(() => {
    if (typeof window === 'undefined') return 1280;
    return window.innerWidth || 1280;
  });
  const [successPulse, setSuccessPulse] = React.useState(false);
  const imagePreviewUrl = useMemo(() => {
    if (!selectedImage?.file) return '';
    try {
      return URL.createObjectURL(selectedImage.file);
    } catch {
      return '';
    }
  }, [selectedImage]);

  const resolvedToken = useMemo(() => {
    const explicitToken = String(token || '').trim();
    if (explicitToken) return explicitToken;

    try {
      return String(getAuthToken?.() || '').trim();
    } catch {
      return '';
    }
  }, [token]);
  const showHint = !error && !isTooLong && isTooShort && rawLength > 0;
  const isNearLimit = remaining <= 80 && !isTooLong;
  const isMobile = viewportWidth <= 640;
  const isTablet = viewportWidth <= 860;

  useEffect(() => {
    const node = textareaRef.current;
    if (!node) return;

    node.style.height = '0px';
    const nextHeight = Math.min(Math.max(node.scrollHeight, 118), 260);
    node.style.height = `${nextHeight}px`;
  }, [text]);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleResize = () => {
      setViewportWidth(window.innerWidth || 1280);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    try {
      const savedDraft = localStorage.getItem(FEED_COMPOSER_DRAFT_KEY);
      if (!savedDraft) return;
      setText(savedDraft);
    } catch {
      // ignore storage failures
    }
  }, [setText]);

  useEffect(() => {
    try {
      if (text) {
        localStorage.setItem(FEED_COMPOSER_DRAFT_KEY, text);
      } else {
        localStorage.removeItem(FEED_COMPOSER_DRAFT_KEY);
      }
    } catch {
      // ignore storage failures
    }
  }, [text]);

  useEffect(() => {
    return () => {
      if (imagePreviewUrl) {
        try {
          URL.revokeObjectURL(imagePreviewUrl);
        } catch {
          // ignore revoke failures
        }
      }
    };
  }, [imagePreviewUrl]);

  useEffect(() => {
    if (!successPulse) return undefined;

    const timeout = window.setTimeout(() => {
      setSuccessPulse(false);
    }, 1000);

    return () => window.clearTimeout(timeout);
  }, [successPulse]);

  const footerMeta = useMemo(() => {
    if (uploadingImage) return feedText('Subiendo imagen...', 'Uploading image...');
    if (successPulse) return feedText('Publicado con éxito', 'Posted successfully');
    if (imageError) return imageError;
    if (error) return feedText('Revisá el texto antes de publicar', 'Review the text before posting');
    if (selectedImage?.upload?.id) return feedText('Imagen lista para publicar', 'Image ready to post');
    if (rawLength > 0) return `${rawLength} / ${maxLength}`;
    return '';
  }, [uploadingImage, successPulse, imageError, error, selectedImage, rawLength, maxLength]);

  const handlePickImage = async (event) => {
    const file = event?.target?.files?.[0] || null;
    if (!file) return;

    setImageError('');

    if (!resolvedToken) {
      setImageError(feedText('No se pudo autenticar la subida de imagen.', 'The image upload could not be authenticated.'));
      event.target.value = '';
      return;
    }

    try {
      setUploadingImage(true);

      if (selectedImage?.upload?.id) {
        try {
          await deleteFeedUpload({ token: resolvedToken, uploadId: selectedImage.upload.id });
        } catch {
          // ignore cleanup failures
        }
      }

      const response = await uploadFeedImage({ token: resolvedToken, file });
      setSelectedImage({
        file,
        upload: response?.upload || null,
      });
    } catch (uploadError) {
      setSelectedImage(null);
      setImageError(uploadError?.message || feedText('No se pudo subir la imagen.', 'The image could not be uploaded.'));
    } finally {
      setUploadingImage(false);
      if (event?.target) {
        event.target.value = '';
      }
    }
  };

  const handleRemoveImage = async () => {
    setImageError('');

    const uploadId = selectedImage?.upload?.id || null;
    setSelectedImage(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    if (!uploadId || !resolvedToken) return;

    try {
      await deleteFeedUpload({ token: resolvedToken, uploadId });
    } catch {
      // ignore cleanup failures
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (disabled || uploadingImage) return;
    if (!canSubmit) {
      console.log('[FeedComposer.handleSubmit.blocked]', {
        canSubmit,
        text,
        rawLength,
        selectedImage,
        selectedUploadId: selectedImage?.upload?.id || null,
        error,
        imageError,
        isTooShort,
        isTooLong,
      });
    }

    console.log('[FeedComposer.handleSubmit]', {
      text,
      selectedImage,
      uploadId: selectedImage?.upload?.id || null,
      upload: selectedImage?.upload || null,
    });

    const result = await submit({
      uploadId: selectedImage?.upload?.id || null,
      upload: selectedImage?.upload || null,
    });

    if (result?.ok) {
      try {
        localStorage.removeItem(FEED_COMPOSER_DRAFT_KEY);
      } catch {
        // ignore storage failures
      }
      setSelectedImage(null);
      setImageError('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setSuccessPulse(true);
    }
  };

  return (
    <form
      className={`feed-composer ${className}`.trim()}
      onSubmit={handleSubmit}
      style={{ width: '100%' }}
    >
      <div
        className="feed-composer__inner"
        style={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: isMobile ? 18 : 24,
          border: successPulse
            ? '1px solid rgba(255,224,130,0.22)'
            : error
              ? '1px solid rgba(255,88,88,0.18)'
              : '1px solid rgba(255,255,255,0.07)',
          background:
            isMobile
              ? 'linear-gradient(180deg, rgba(255,255,255,0.07), rgba(255,255,255,0.03))'
              : 'linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0.045))',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          padding: isMobile ? 12 : isTablet ? 13 : 15,
          boxShadow: successPulse
            ? '0 18px 38px rgba(8,5,20,0.16), 0 0 0 1px rgba(255,255,255,0.03) inset, 0 0 32px rgba(255,224,130,0.12)'
            : error
              ? '0 18px 38px rgba(8,5,20,0.16), 0 0 0 1px rgba(255,255,255,0.03) inset, 0 0 26px rgba(255,88,88,0.08)'
              : '0 16px 34px rgba(8,5,20,0.14), 0 0 0 1px rgba(255,255,255,0.03) inset',
          transition: 'border-color 180ms ease, box-shadow 180ms ease, background 180ms ease',
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(220px 110px at 0% 0%, rgba(255,224,130,0.10), rgba(255,224,130,0) 60%), radial-gradient(200px 100px at 100% 0%, rgba(170,124,255,0.10), rgba(170,124,255,0) 58%)',
            pointerEvents: 'none',
          }}
        />

        <div className="feed-composer__header" style={{ position: 'relative', zIndex: 1 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 10,
              flexWrap: 'wrap',
              marginBottom: isMobile ? 10 : 12,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <h3
                className="feed-composer__title"
                style={{
                  margin: 0,
                  color: '#FFF2BF',
                  fontSize: isMobile ? 15 : 18,
                  fontWeight: 1000,
                  letterSpacing: -0.18,
                  lineHeight: 1.05,
                }}
              >
                {feedText('Nuevo post', 'New post')}
              </h3>
              
            </div>

            <div
              className={`feed-composer__counter ${isNearLimit ? 'is-warning' : ''} ${isTooLong ? 'is-danger' : ''}`.trim()}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: isMobile ? 48 : 54,
                padding: isMobile ? '6px 9px' : '7px 10px',
                borderRadius: 999,
                background: isTooLong
                  ? 'rgba(255,88,88,0.10)'
                  : isNearLimit
                    ? 'rgba(255,224,130,0.12)'
                    : 'rgba(255,255,255,0.06)',
                border: isTooLong
                  ? '1px solid rgba(255,88,88,0.16)'
                  : isNearLimit
                    ? '1px solid rgba(255,224,130,0.16)'
                    : '1px solid rgba(255,255,255,0.06)',
                color: isTooLong
                  ? '#FFD9D9'
                  : isNearLimit
                    ? '#FFE39A'
                    : 'rgba(255,245,220,0.78)',
                fontSize: isMobile ? 10.5 : 11.5,
                fontWeight: 1000,
                letterSpacing: 0.18,
                textAlign: 'center',
                whiteSpace: 'nowrap',
              }}
            >
              {remaining}
            </div>
          </div>
        </div>

        <div
          className="feed-composer__field-wrap"
          style={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 0,
          }}
        >

          <div style={{ flex: 1, minWidth: 0 }}>
            <textarea
              id="feedPostText"
              name="feedPostText"
              ref={textareaRef}
              className={`feed-composer__textarea ${error ? 'has-error' : ''}`.trim()}
              value={text}
              onChange={handleChange}
              onKeyDown={onKeyDown}
              placeholder={resolvedPlaceholder}
              rows={4}
              maxLength={maxLength + 50}
              disabled={disabled || submitting}
              style={{
                width: '100%',
                minHeight: isMobile ? 100 : 118,
                height: isMobile ? 100 : 118,
                resize: 'none',
                overflowY: rawLength > 240 ? 'auto' : 'hidden',
                borderRadius: isMobile ? 14 : 18,
                border: error
                  ? '1px solid rgba(255,88,88,0.20)'
                  : '1px solid rgba(255,255,255,0.07)',
                background: isMobile
                  ? 'rgba(255,255,255,0.045)'
                  : 'linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0.055))',
                color: '#FFFDF8',
                padding: isMobile ? '13px 13px 12px' : '16px 16px 14px',
                fontSize: isMobile ? 16 : 14.8,
                lineHeight: isMobile ? 1.56 : 1.64,
                WebkitTextSizeAdjust: '100%',
                zoom: 1,
                outline: 'none',
                transition: 'border-color 160ms ease, box-shadow 160ms ease, background 160ms ease',
                caretColor: '#FFD700',
                boxShadow: error
                  ? '0 0 0 1px rgba(255,88,88,0.05) inset'
                  : '0 6px 14px rgba(255,255,255,0.03) inset, 0 0 0 1px rgba(255,255,255,0.02)',
              }}
            />
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={handlePickImage}
            style={{ display: 'none' }}
          />
        </div>

        <div
          style={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
            flexWrap: 'wrap',
            marginTop: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || submitting || uploadingImage}
              style={{
                borderRadius: 999,
                border: '1px solid rgba(255,255,255,0.08)',
                background: 'rgba(255,255,255,0.06)',
                color: '#FFF5DC',
                padding: isMobile ? '9px 12px' : '10px 14px',
                fontSize: isMobile ? 12 : 12.5,
                fontWeight: 800,
                cursor: 'pointer',
                opacity: disabled || submitting || uploadingImage ? 0.68 : 1,
              }}
            >
              {uploadingImage ? feedText('Subiendo imagen...', 'Uploading image...') : selectedImage?.upload?.id ? feedText('Cambiar imagen', 'Change image') : feedText('Agregar imagen', 'Add image')}
            </button>

            {selectedImage?.upload?.id ? (
              <button
                type="button"
                onClick={handleRemoveImage}
                disabled={disabled || submitting || uploadingImage}
                style={{
                  borderRadius: 999,
                  border: '1px solid rgba(255,120,120,0.16)',
                  background: 'rgba(255,88,88,0.08)',
                  color: '#FFDADA',
                  padding: isMobile ? '9px 12px' : '10px 14px',
                  fontSize: isMobile ? 12 : 12.5,
                  fontWeight: 800,
                  cursor: 'pointer',
                  opacity: disabled || submitting || uploadingImage ? 0.68 : 1,
                }}
              >
                Quitar
              </button>
            ) : null}
          </div>
        </div>

        {selectedImage?.upload?.id && imagePreviewUrl ? (
          <div
            style={{
              position: 'relative',
              zIndex: 1,
              marginTop: 12,
              borderRadius: isMobile ? 14 : 18,
              overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.08)',
              background: 'rgba(255,255,255,0.04)',
            }}
          >
            <img
              src={imagePreviewUrl}
              alt="Vista previa de la imagen del post"
              style={{
                display: 'block',
                width: '100%',
                maxHeight: isMobile ? 240 : 320,
                objectFit: 'cover',
              }}
            />
          </div>
        ) : null}

        {imageError ? (
          <p
            className="feed-composer__message feed-composer__message--error"
            style={{
              position: 'relative',
              zIndex: 1,
              margin: '9px 2px 0',
              color: '#FFDADA',
              fontSize: isMobile ? 11.5 : 12.5,
              fontWeight: 700,
            }}
          >
            {imageError}
          </p>
        ) : null}

        {error ? (
          <p
            className="feed-composer__message feed-composer__message--error"
            style={{
              position: 'relative',
              zIndex: 1,
              margin: '9px 2px 0',
              color: '#FFDADA',
              fontSize: isMobile ? 11.5 : 12.5,
              fontWeight: 700,
            }}
          >
            {error}
          </p>
        ) : null}

        {showHint ? (
          <p
            className="feed-composer__message"
            style={{
              position: 'relative',
              zIndex: 1,
              margin: '9px 2px 0',
              color: 'rgba(245,240,255,0.64)',
              fontSize: isMobile ? 11.5 : 12.5,
              fontWeight: 600,
            }}
          >
            {feedText(`Tu publicación debe tener al menos ${minLength} caracteres.`, `Your post must be at least ${minLength} characters long.`)}
          </p>
        ) : null}

        <div
          className="feed-composer__footer"
          style={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
            flexWrap: 'wrap',
            marginTop: isMobile ? 12 : 14,
          }}
        >
          <div
            style={{
              fontSize: isMobile ? 10.5 : 11,
              opacity: successPulse ? 0.94 : 0.64,
              textAlign: 'right',
              color: successPulse ? '#FFE39A' : error ? '#FFDADA' : '#F7F2FF',
              fontWeight: 700,
              letterSpacing: 0.12,
              whiteSpace: 'nowrap',
            }}
          >
            {footerMeta}
          </div>

          <button
  type="submit"
  className="feed-composer__submit"
  onClick={() => {
    console.log('[FeedComposer.submitButton.click]', {
      disabledProp: disabled,
      submitting,
      uploadingImage,
      canSubmit,
      text,
      rawLength,
      selectedImage,
      selectedUploadId: selectedImage?.upload?.id || null,
      error,
      imageError,
      isTooShort,
      isTooLong,
    });
  }}
  disabled={disabled || submitting || uploadingImage}
  style={{
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 999,
    padding: isMobile ? '10px 16px' : '11px 20px',
    minWidth: isMobile ? 134 : 152,
    fontWeight: 1000,
    fontSize: isMobile ? 12.5 : 13.5,
    letterSpacing: 0.26,
    color: '#2E1A00',
    background:
      'linear-gradient(135deg, #FFE28A 0%, #FFD76A 32%, #FFC94A 62%, #FFB938 100%)',
    border: '1px solid rgba(255,224,130,0.52)',
    boxShadow:
      '0 10px 22px rgba(0,0,0,0.18), 0 0 16px rgba(255,224,130,0.22), 0 0 8px rgba(255,255,255,0.18) inset',
    cursor: 'pointer',
    transition: 'transform 0.18s ease, filter 0.18s ease, box-shadow 0.18s ease, opacity 0.18s ease',
    transform: successPulse ? 'translateY(-1px)' : submitting ? 'scale(0.985)' : 'scale(1)',
    filter: disabled || submitting || uploadingImage
      ? 'saturate(0.9)'
      : successPulse
        ? 'brightness(1.03)'
        : submitting
          ? 'brightness(0.94)'
          : 'brightness(1)',
    opacity: disabled || submitting || uploadingImage ? 0.76 : 1,
    whiteSpace: 'nowrap',
  }}
>
  <span
    style={{
      position: 'relative',
      zIndex: 2,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 7,
    }}
  >
    <span aria-hidden="true" style={{ fontSize: 13, lineHeight: 1 }}>✦</span>
    {submitting ? feedText('Publicando...', 'Posting...') : successPulse ? feedText('Publicado', 'Posted') : resolvedSubmitLabel}
  </span>

  <span
    aria-hidden="true"
    style={{
      position: 'absolute',
      inset: 0,
      borderRadius: 'inherit',
      background:
        'linear-gradient(120deg, transparent, rgba(255,255,255,0.38), transparent)',
      transform: 'translateX(-120%)',
      animation: 'feed-gold-sheen 2.8s ease-in-out infinite',
    }}
  />
</button>
        </div>
      </div>
      <style>{`
        @keyframes feed-gold-sheen {
          0% { transform: translateX(-120%); opacity: 0; }
          30% { opacity: 0.4; }
          60% { opacity: 0.15; }
          100% { transform: translateX(120%); opacity: 0; }
        }

        .feed-composer__textarea::placeholder {
          color: rgba(255, 245, 220, 0.46);
        }

        .feed-composer__textarea:focus {
          border-color: rgba(255, 224, 130, 0.20) !important;
          box-shadow:
            0 0 0 1px rgba(255,224,130,0.06) inset,
            0 0 0 3px rgba(255,224,130,0.04),
            0 8px 18px rgba(255,255,255,0.04) inset !important;
          background: linear-gradient(180deg, rgba(255,255,255,0.11), rgba(255,255,255,0.055));
        }

        .feed-composer__submit:hover,
        .feed-composer__submit:focus-visible {
          transform: translateY(-1px);
          box-shadow:
            0 12px 24px rgba(0,0,0,0.20),
            0 0 20px rgba(255,224,130,0.28),
            0 0 8px rgba(255,255,255,0.20) inset;
        }

        @media (max-width: 640px) {
          .feed-composer__submit:hover,
          .feed-composer__submit:focus-visible {
            transform: none;
          }
        }
      `}</style>
    </form>
  );
}
